import { useState } from "react";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import * as crmApi from "../../api/crmApi";
import { crmErrorMessage } from "../../api/crmErrors";

/**
 * Preview and "Send test to me" for ONE draft email — `draft` is the request body of
 * `POST /api/automation/preview` (`{kind, subject, body_html, sequence_id?, offset?,
 * book_again_button?}`), built by the caller from what is on screen right now.
 *
 * A preview belongs to the draft it was rendered from: `email` is null as soon as the
 * draft differs from that one (an edit after Preview), so the screen never shows an email
 * the practitioner is no longer writing.
 */
export default function useEmailPreview(draft) {
  const t = useT("crm");
  const tc = useT("common");
  const key = JSON.stringify(draft);
  const [rendered, setRendered] = useState({ key: null, email: null });
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const email = await crmApi.previewEmail(draft);
      setRendered({ key, email });
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc));
    } finally {
      setLoading(false);
    }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const res = await crmApi.sendEmailTest(draft);
      // `queued`: recorded in the outbox, goes out once email sending is configured.
      toast.success(t(res?.queued ? "seq.sendTestQueued" : "seq.sendTestSent"));
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc));
    } finally {
      setSending(false);
    }
  };

  return { email: rendered.key === key ? rendered.email : null, loading, load, sending, sendTest };
}
